import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import {
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  Cell,
} from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const formatPercent = (value) =>
  value === undefined || value === null
    ? '-'
    : Number((Math.floor(Number(value) * 1000) / 10).toFixed(1));

const gradeHorse = (p, ev) => {
  if (p === undefined || ev === undefined) return '-';
  // probability: 0-1, expectedValue: 倍率
  if (p >= 0.28 && ev >= 1.3) return 'S';
  if (p >= 0.2 && ev >= 1.15) return 'A';
  if (p >= 0.13 && ev >= 1.05) return 'B';
  if (p >= 0.07 && ev >= 0.95) return 'C';
  if (p >= 0.03 || ev >= 0.9) return 'D';
  return 'E';
};

const buildChartData = (list) =>
  (list?.tanpuku_df ?? [])
    .map((r) => {
      const p = Number(r.p);
      const probabilityPct = formatPercent(p);
      const expectedValue = Number(r.ev);
      const gradeFromServer = (() => {
        const raw =
          r.grade ||
          r.Grade ||
          r.ranks ||
          r.rank ||
          (typeof r.grade === 'string' && r.grade.trim());
        if (!raw) return undefined;
        const g = String(raw).trim().toUpperCase();
        return ['S', 'A', 'B', 'C', 'D', 'E'].includes(g) ? g : undefined;
      })();
      const grade = gradeFromServer || gradeHorse(p, expectedValue);
      return {
        umaban: r.umaban,
        pCal: p,
        probability: probabilityPct,
        odds: Number(r.tansho_odds),
        ev: expectedValue,
        evPercent: formatPercent(expectedValue),
        prediction: Number(r.pick) === 1 || Number(r.ev_gt_1_plus_mu) === 1,
        grade,
      };
    })
    .sort((a, b) => b.pCal - a.pCal);

function ShibaPage() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [shibaList, setShibaList] = useState({});
  const [loading, setLoading] = useState(true);
  const [raceList, setRaceList] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [raceLoading, setRaceLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [courseMemo, setCourseMemo] = useState(null);
  const [courseMemoStatus, setCourseMemoStatus] = useState('idle'); // idle | loading | success | error

  // キャプチャ用
  const chartRef = useRef(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [sPicksAll, setSPicksAll] = useState([]);
  const [sPicksLoading, setSPicksLoading] = useState(false);
  const [sPicksOpen, setSPicksOpen] = useState(false);
  const [courseMemoOpen, setCourseMemoOpen] = useState(false);

  const API_BASE = '';

  const pct = (v) => `${v}%`;

  const gradeColors = {
    S: '#d32f2f',
    A: '#f57c00',
    B: '#fbc02d',
    C: '#1976d2',
    D: '#7b1fa2',
    E: '#9e9e9e',
  };

  const sortedHorses = useMemo(() => buildChartData(shibaList), [shibaList]);
  const sRankHorses = useMemo(() => sortedHorses.filter((h) => h.grade === 'S'), [sortedHorses]);
  const chartData = useMemo(() => sortedHorses.slice(0, 8), [sortedHorses]);
  const barColor = (grade) => gradeColors[grade] || '#8884d8';
  const raceName = shibaList?.race_name;
  const courseMemoText = courseMemo?.text ?? courseMemo?.memo;
  // 日付一覧取得
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/chiriri_keiba_new_model/available_dates`);
        const dates = res.data.dates;
        setAvailableDates(dates);
        if (dates.length > 0) {
          setSelectedDate(dates[0]);
        }
      } catch (error) {
        console.error('日付一覧取得失敗:', error);
      }
    };
    fetchDates();
  }, []);

  // レース一覧取得
  useEffect(() => {
    if (!selectedDate) return;

    const fetchRaceList = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/chiriri_keiba_new_model/race_list`, {
          params: { date: selectedDate },
        });
        setRaceList(res.data.race_list);
        // 日付変更時に選択中レースをリセット
        setSelectedRace(null);
        setShibaList({});
        setCourseMemo(null);
        setCourseMemoStatus('idle');
      } catch (error) {
        console.error('データ取得失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRaceList();
  }, [selectedDate]);

  useEffect(() => {
    const fetchAllSPicks = async () => {
      if (!raceList || raceList.length === 0) return;
      setSPicksLoading(true);
      try {
        const results = [];
        // 順次取得で負荷を抑える
        for (const race of raceList) {
          const raceName = typeof race === 'string' ? race : race?.race_name || race?.race_key;
          const raceKey = typeof race === 'string' ? race : race?.race_key || race?.race_name;
          if (!raceKey) continue;
          try {
            const res = await axios.get(`${API_BASE}/api/chiriri_keiba_new_model/predictions`, {
              params: { race_info: raceKey, date: selectedDate },
            });
            const horses = buildChartData(res.data);
            const sList = horses.filter((h) => h.grade === 'S');
            sList.forEach((h) => {
              results.push({
                raceInfo: raceKey,
                raceName: raceName || res.data?.race_name || raceKey,
                umaban: h.umaban,
                odds: h.odds,
                probability: h.probability,
                evPercent: h.evPercent,
              });
            });
          } catch (e) {
            console.error(`Sピック取得失敗: ${raceKey}`, e);
          }
        }
        setSPicksAll(results);
      } finally {
        setSPicksLoading(false);
      }
    };
    fetchAllSPicks();
  }, [raceList, selectedDate]);

  const fetchCourseMemo = async (raceInfo) => {
    if (!raceInfo) return;
    setCourseMemoStatus('loading');
    try {
      const res = await axios.get(`${API_BASE}/api/course_memo`, {
        params: { race_info: raceInfo, race_date: selectedDate },
      });
      setCourseMemo(res.data);
      setCourseMemoStatus('success');
    } catch (error) {
      console.error('コースメモ取得失敗:', error);
      setCourseMemo(null);
      setCourseMemoStatus('error');
    }
  };

  const fetchRaceData = async (race_info) => {
    const raceKey =
      typeof race_info === 'string'
        ? race_info
        : race_info?.race_key || race_info?.race_name;
    const raceName =
      typeof race_info === 'string'
        ? race_info
        : race_info?.race_name || race_info?.race_key;
    setSelectedRace(raceKey || raceName);
    setRaceLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/chiriri_keiba_new_model/predictions`, {
        params: { race_info: raceKey || raceName, date: selectedDate },
      }
      );
      setShibaList(res.data);
      setSaveMsg(null);
      await fetchCourseMemo(raceName);
    } catch (error) {
      console.error('データ取得失敗:', error);
    } finally {
      setRaceLoading(false);
    }
  };

  if (loading) return <div>読み込み中...</div>;

  return (
    <Box p={{ xs: 0, sm: 4 }} sx={{ width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="date-select-label">日付選択</InputLabel>
          <Select
            labelId="date-select-label"
            value={selectedDate}
            label="日付選択"
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            {availableDates.map((date) => (
              <MenuItem key={date} value={date}>
                {date}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        {raceList.map((race) => {
          const raceName = typeof race === 'string' ? race : race?.race_name;
          const raceKey = typeof race === 'string' ? race : race?.race_key || race?.race_name;
          const roughness = typeof race === 'string' ? null : race?.roughness;
          if (!raceName) return null;
          return (
          <Button
            key={raceKey || raceName}
            variant="contained"
            color="primary"
            onClick={() => fetchRaceData({ race_key: raceKey, race_name: raceName })}
            sx={{
              mr: 1,
              mb: 1,
              backgroundColor: selectedRace === (raceKey || raceName) ? theme.palette.secondary.main : undefined,
            }}
          >
            {roughness ? `${raceName} [${roughness}]` : raceName}
          </Button>
          );
        })}
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 } }}>
        <Button
          onClick={() => setSPicksOpen((prev) => !prev)}
          endIcon={sPicksOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            justifyContent: 'flex-start',
            width: '100%',
            color: 'text.primary',
            textTransform: 'none',
            mb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
            全レースのS評価一覧
          </Typography>
        </Button>
        <Collapse in={sPicksOpen} timeout="auto" unmountOnExit>
          {sPicksLoading ? (
            <Typography variant="body2" color="text.secondary">
              読み込み中...
            </Typography>
          ) : sPicksAll.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              S評価の馬はまだいません。
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>レース</TableCell>
                  <TableCell align="center">馬番</TableCell>
                  <TableCell align="center">オッズ</TableCell>
                  <TableCell align="center">単勝確率</TableCell>
                  <TableCell align="center">期待値</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sPicksAll.map((row, idx) => (
                  <TableRow key={`${row.raceInfo}-${row.umaban}-${idx}`}>
                    <TableCell>{row.raceName}</TableCell>
                    <TableCell align="center">{row.umaban}</TableCell>
                    <TableCell align="center">{row.odds}</TableCell>
                    <TableCell align="center">{row.probability}%</TableCell>
                    <TableCell align="center">{row.evPercent}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Collapse>
      </Paper>

      {saveMsg && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={saveMsg.type}>{saveMsg.text}</Alert>
        </Box>
      )}

      {raceLoading && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" icon={<CircularProgress size={16} />}>
            レース情報を読み込み中...
          </Alert>
        </Box>
      )}

      {courseMemoStatus === 'success' && courseMemoText && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 } }}>
          <Button
            onClick={() => setCourseMemoOpen((prev) => !prev)}
            endIcon={courseMemoOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              justifyContent: 'flex-start',
              width: '100%',
              color: 'text.primary',
              textTransform: 'none',
              mb: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              今日の馬場・コースの見立て
            </Typography>
          </Button>
          <Collapse in={courseMemoOpen} timeout="auto" unmountOnExit>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {courseMemoText}
            </Typography>
            {courseMemo.generated_at && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                作成: {courseMemo.generated_at}
              </Typography>
            )}
          </Collapse>
        </Paper>
      )}
      {courseMemoStatus === 'error' && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning">
            馬場・コースのコメントがまだ登録されていません。朝のバッチで生成したメモを `course_memo`
            に保存するとここに表示されます。
          </Alert>
        </Box>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 1 }}>
          今レースS評価
        </Typography>
        {sRankHorses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            S評価の馬はまだいません。
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>馬番</TableCell>
                <TableCell>オッズ</TableCell>
                <TableCell>単勝確率</TableCell>
                <TableCell>期待値</TableCell>
                <TableCell>購入可否</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sRankHorses.map((h, idx) => (
                <TableRow key={h.id ?? `${h.umaban}-${idx}`}>
                  <TableCell>{h.umaban}</TableCell>
                  <TableCell>{h.odds}</TableCell>
                  <TableCell>{h.probability}%</TableCell>
                  <TableCell>{h.evPercent}%</TableCell>
                  <TableCell align="center">{h.prediction ? '◎' : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {chartData.length > 0 && (
        <Box
          sx={{
            width: { xs: '100%', sm: '100%' },
            ml: { xs: 'calc(50% - 50vw)', sm: 0 },
            mr: { xs: 'calc(50% - 50vw)', sm: 0 },
            height: { xs: 420, sm: 380 },
            mb: { xs: 8, sm: 6 },
          }}
        >
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              単勝確率（%）上位8頭 {raceName ? `（${raceName}）` : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              色の意味
            </Typography>
            {['S', 'A', 'B', 'C', 'D', 'E'].map((g) => (
              <Stack key={g} direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: gradeColors[g],
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {g}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* ← この可視グラフのアニメ完了を待って撮る */}
          <div ref={chartRef}>
            <ResponsiveContainer width="100%" height={isXs ? 440 : 380}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 24, left: 8, bottom: isXs ? 60 : 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="umaban"
                  interval={0}
                  tick={{ fontSize: isXs ? 10 : 18 }}
                  height={isXs ? 44 : 30}
                  angle={isXs ? -35 : 0}
                  textAnchor={isXs ? 'end' : 'middle'}
                />
                <YAxis domain={[0, 100]} tickFormatter={pct} tick={{ fontSize: isXs ? 10 : 18 }} />
                <Tooltip
                  wrapperStyle={{ fontSize: isXs ? '0.7rem' : '0.85rem' }}
                  formatter={(value, name) => {
                    if (name === 'probability') return [`${value}%`, '単勝確率'];
                    if (name === 'grade') return [value, '評価'];
                    return [value, name];
                  }}
                  labelFormatter={(label, payload) => {
                    const p = payload?.[0]?.payload;
                    return `馬番 ${label}${p?.prediction ? '（◎）' : ''}`;
                  }}
                />
                <Bar dataKey="probability" name="単勝確率">
                  <LabelList
                    dataKey="probability"
                    position="top"
                    formatter={(v) => `${v}%`}
                    fontSize={isXs ? 8 : 12}
                  />
                  {chartData.map((d, i) => (
                    <Cell key={`cell-${i}`} fill={barColor(d.grade)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ mt: { xs: 0, sm: 20 } }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                オッズ
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                単勝確率
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                期待値
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                推奨馬
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chartData.map((tanpuku, idx) => (
              <TableRow key={tanpuku.id ?? `${tanpuku.umaban}-${idx}`}>
                <TableCell align="center">{tanpuku.umaban}番</TableCell>
                <TableCell align="center">{tanpuku.odds}</TableCell>
                <TableCell align="center">{tanpuku.probability}%</TableCell>
                <TableCell align="center">{tanpuku.evPercent}%</TableCell>
                <TableCell align="center">{tanpuku.prediction ? '◎' : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ShibaPage;
